/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Redirect } from 'aurelia-router';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { IAssessmentAttachmentOptions } from 'app/core/components/assessments/assessment-attachment';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class CaseAssessmentAttachmentPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected attachmentId: string | null = null;
    protected caseId: string | null = null;
    protected readonly: boolean = false;

    protected assessmentConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        backRouteName: 'cm.case.assessments',
        onBeforeSessionSave: this.beforeSessionSave.bind(this)
    };

    protected taskConfig: ITasksConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        addTargetProperty: this.addCaseProperty.bind(this)
    };

    private case: Readonly<CM.Case> | null = null;

    protected options: IAssessmentAttachmentOptions = {
        showHeader: true,
        showToc: true,
        showBack: true,
        showActions: true,
        showMember: true,
        showMemberSummaryLink: false,
        showTarget: true,
        showTargetSummaryLink: false,
        forceRequiredQuestions: false,
        canReopen: true,
        stickyActions: true,
        isTaskDisabled: false
    };

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AssessmentService,
        private readonly context: CasePageContext,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, attachmentId }: { caseId: string, attachmentId: string }): Promise<Redirect | true> {
        if(!(caseId && attachmentId)) {
            throw new Error('Required parameters [caseId] and/or [attachmentId] were not found');
        }

        try {
            this.caseId = caseId;
            this.attachmentId = attachmentId;
            if(!this.context.isCaseLoaded || !this.context.case.member?.description || !this.context.case.member?.name?.full || !this.context.case.number || this.context.case.id !== this.caseId) {
                const includes = ['member'];
                const fields = ['member.description', 'member.name.full', 'number'];
                await this.context.loadCase(this.caseId, includes, fields);
            }
            this.case = this.context.case;

            this.service.configure(this.assessmentConfig);

            const response = await this.service.loadAttachment(this.attachmentId, undefined, ['case.id']);
            const attachment: CM.CaseAssessmentAttachment = response.results[0];
            const attachmentCaseId = attachment.case?.id;
            if(attachmentCaseId !== this.caseId) {
                // reroute user if task does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('core:assessment.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: this.context.case.number
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect(`cm/case/${caseId}/assessments`);
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate(): Promise<void> {
        this.checkPermissions();
    }


    ////****************** Events ***************************************************////
    protected async beforeSessionSave(session: CM.CaseAssessmentSession): Promise<boolean> {
        // Placeholder/example
        return true;
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
        this.readonly = this.context?.caseIsReadOnly(canEditClosedCase);
        this.options.showTargetSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        if(!!this.case?.member) {
            this.options.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        }
    }

    protected addCaseProperty(task: CM.CaseTask): void {
        task.case = { id: this.caseId };
    }


    ////****************** Service Calls ********************************************////

}
